import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import Button from '../components/ui/Button';
import { PaletteIcon, TrendingUpIcon, BuildingIcon, ShoppingBagIcon, ArrowRightIcon } from 'lucide-react';
const Industries = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const industries = [{
    icon: <PaletteIcon size={40} data-id="element-483" />,
    title: 'Arts & Culture',
    description: "We help cultural organizations understand audience preferences, optimize experiences, and increase engagement across Toronto's diverse communities.",
    image: 'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1160&q=80',
    caseStudy: 'Diversity in Toronto Art Engagement'
  }, {
    icon: <TrendingUpIcon size={40} data-id="element-484" />,
    title: 'Sports & Events',
    description: 'Our research helps sports teams, venues, and event organizers optimize fan experiences, increase loyalty, and maximize sponsorship value.',
    image: 'https://images.unsplash.com/photo-1504450758481-7338eba7524a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1169&q=80',
    caseStudy: 'Toronto Raptors Fan Culture'
  }, {
    icon: <BuildingIcon size={40} data-id="element-485" />,
    title: 'Urban Planning & Smart Cities',
    description: 'We provide insights on housing preferences, transit usage, public space design, and smart city technologies to help create more livable urban environments.',
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
    caseStudy: 'Consumer Behavior in Urban Cores Post-COVID'
  }, {
    icon: <ShoppingBagIcon size={40} data-id="element-486" />,
    title: 'Retail & Consumer Behavior',
    description: "We help retailers and consumer brands understand shopping behaviors, preferences, and trends in Toronto's multicultural marketplace.",
    image: 'https://images.unsplash.com/photo-1573164574572-cb89e39749b4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1169&q=80',
    caseStudy: 'Multicultural Shopping Preferences in the GTA'
  }];
  return <div className="pt-20" data-id="element-487">
      {/* Hero Section */}
      <section className="bg-neutral-light py-20" data-id="element-488">
        <div className="container mx-auto px-4 md:px-6" data-id="element-489">
          <div className="max-w-3xl mx-auto text-center" data-id="element-490">
            <motion.h1 initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="text-4xl md:text-5xl font-display font-bold mb-6" data-id="element-491">
              Industries <span className="text-toronto-blue" data-id="element-492">We Serve</span>
            </motion.h1>
            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.2,
            duration: 0.5
          }} className="text-xl text-neutral-dark/80" data-id="element-493">
              Specialized research solutions for key sectors in Toronto's
              dynamic economy.
            </motion.p>
          </div>
        </div>
      </section>
      {/* Industries Section */}
      <section className="py-16 bg-white" data-id="element-494">
        <div className="container mx-auto px-4 md:px-6" data-id="element-495">
          {industries.map((industry, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="mb-20 last:mb-0" data-id="element-496">
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`} data-id="element-497">
                <div data-id="element-498">
                  <div className="bg-toronto-blue/10 text-toronto-blue p-4 rounded-full w-20 h-20 flex items-center justify-center mb-6" data-id="element-499">
                    {industry.icon}
                  </div>
                  <h2 className="text-3xl font-display font-bold mb-4" data-id="element-500">
                    {industry.title}
                  </h2>
                  <p className="text-lg text-neutral-dark/80 mb-6" data-id="element-501">
                    {industry.description}
                  </p>
                  <div className="bg-neutral-light p-6 rounded-lg mb-8" data-id="element-502">
                    <h3 className="text-xl font-semibold mb-3" data-id="element-503">Our Approach</h3>
                    <ul className="space-y-2" data-id="element-504">
                      {['Specialized research methodologies for this sector', 'Deep understanding of industry trends and challenges', 'Multicultural research expertise', 'Actionable insights and recommendations'].map((item, i) => <li key={i} className="flex items-center" data-id="element-505">
                          <div className="w-2 h-2 bg-toronto-blue rounded-full mr-2" data-id="element-506"></div>
                          <span data-id="element-507">{item}</span>
                        </li>)}
                    </ul>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4" data-id="element-508">
                    <Button variant="primary" data-id="element-509">Industry Solutions</Button>
                    <Button variant="outline" className="inline-flex items-center" data-id="element-510">
                      Featured Case Study: {industry.caseStudy}{' '}
                      <ArrowRightIcon size={16} className="ml-2" data-id="element-511" />
                    </Button>
                  </div>
                </div>
                <div data-id="element-512">
                  <img src={industry.image} alt={industry.title} className="rounded-lg shadow-lg w-full h-auto object-cover" style={{
                maxHeight: '500px'
              }} data-id="element-513" />
                </div>
              </div>
              {index < industries.length - 1 && <div className="border-b border-neutral-light/70 my-20" data-id="element-514"></div>}
            </motion.div>)}
        </div>
      </section>
      {/* Client Logos */}
      <section className="py-16 bg-neutral-light/50" data-id="element-515">
        <div className="container mx-auto px-4 md:px-6" data-id="element-516">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-517">
            <h2 className="text-3xl font-display font-bold mb-4" data-id="element-518">
              Trusted By Industry Leaders
            </h2>
            <p className="text-neutral-dark/80" data-id="element-519">
              We've helped organizations across various sectors gain valuable
              insights into Toronto's diverse markets.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-520">
            {[1, 2, 3, 4].map(item => <motion.div key={item} initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: item * 0.1,
            duration: 0.5
          }} className="bg-white h-24 rounded-lg shadow-sm flex items-center justify-center p-6" data-id="element-521">
                <div className="w-full h-full bg-neutral-dark/10 flex items-center justify-center rounded" data-id="element-522">
                  <span className="text-neutral-dark/50 font-medium" data-id="element-523">
                    Client Logo
                  </span>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-toronto-blue text-white" data-id="element-524">
        <div className="container mx-auto px-4 md:px-6" data-id="element-525">
          <div className="max-w-3xl mx-auto text-center" data-id="element-526">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-6" data-id="element-527">
              Ready to Gain Industry-Specific Insights?
            </h2>
            <p className="text-xl mb-8 opacity-90" data-id="element-528">
              Contact our team to discuss how our specialized research can help
              your organization succeed in Toronto's dynamic marketplace.
            </p>
            <div className="flex flex-wrap justify-center gap-4" data-id="element-529">
              <Button variant="secondary" size="lg" data-id="element-530">
                Schedule a Consultation
              </Button>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10" data-id="element-531">
                View Case Studies
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>;
};
export default Industries;